import React from "react";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../ui/table";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { ChevronRight } from "lucide-react";
import { recoveryStatePresentation } from "../../lib/emailHygieneRecoveryState";

/**
 * EmailHygieneReconciliationQueueTable — DASH-EMAIL-013.
 *
 * Renders the real, combined Email Hygiene Reconciliation queue
 * (EMAIL-HYGIENE-GATE-3A ReconciliationQueueItem[] -- native
 * reconciliation-required recipients PLUS cross-environment propagation
 * records whose aggregate_status is NEEDS_ATTENTION). Mirrors
 * EmailCreativeCatalogTable's row-click-to-open/chevron convention.
 *
 * Matched CRM contacts is rendered as a plain, neutral count — 2 matched
 * contacts is NOT an error, NOT a duplicate, and is never colored red or
 * otherwise flagged merely for exceeding 1 (RECON-002 item 9 / this
 * checkpoint's own explicit requirement).
 *
 * "Available Action" renders ONLY what the backend returned in
 * `available_actions` — never derived from `reconciliation_reason`. An
 * empty `available_actions` array is a valid, reviewable state (the
 * RECON-003 fold-back gap is one real cause, and EVERY propagation row is
 * another, structural one — see the Type column below) and renders an
 * honest "No action available" label, never a fabricated Retry/Force
 * control.
 *
 * Type column (EMAIL-HYGIENE-GATE-3A): distinguishes a native
 * reconciliation row from a "Production Sync" (propagation) row, so an
 * operator never mistakes a downstream synchronization failure for a
 * Qualification ingestion problem — Qualification ingestion already
 * succeeded for every propagation row (see EmailHygieneReconciliationDetail.jsx's
 * own propagation summary banner). `classification`/`candidate_hygiene_state`
 * are native-only concepts (nested under `row.native` now, never at the
 * top level) — a propagation row shows its own canonical hygiene state
 * from `row.propagation` instead.
 */
/**
 * Recovery Status column (EMAIL-HYGIENE-RECOVERY-STATE-001, Phase 1): the
 * backend-derived answer to "what happens next?". Rendered for Native rows
 * only -- a Production Sync row has no Native recovery semantics and shows a
 * neutral dash rather than borrowing a Native label.
 */
function RecoveryStateCell({ recoveryState, testId }) {
  const presentation = recoveryStatePresentation(recoveryState);
  if (!presentation) {
    return (
      <TableCell className="text-xs text-slate-500" data-testid={testId}>
        —
      </TableCell>
    );
  }
  return (
    <TableCell data-testid={testId}>
      <Badge className={`${presentation.className} text-xs`} title={presentation.explanation || undefined}>
        {presentation.label}
      </Badge>
    </TableCell>
  );
}

const RECORD_TYPE_BADGE = {
  native: { label: "Native", className: "bg-slate-500/20 text-slate-300" },
  propagation: { label: "Production Sync", className: "bg-amber-500/20 text-amber-300" },
};
function formatRelativeAge(isoString) {
  if (!isoString) return "—";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "—";
  const diffMs = Date.now() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

function AvailableActionCell({ actions, testId }) {
  const first = (actions || [])[0];
  if (!first) {
    return (
      <TableCell className="text-xs text-slate-500" data-testid={testId}>
        No action available
      </TableCell>
    );
  }
  return (
    <TableCell data-testid={testId}>
      <Badge className="bg-emerald-500/20 text-emerald-300 text-xs" title={first.description}>
        {first.label}
      </Badge>
    </TableCell>
  );
}

export default function EmailHygieneReconciliationQueueTable({ items, onSelect }) {
  const rows = items || [];

  if (rows.length === 0) {
    return (
      <Card className="glass border-white/10" data-testid="email-hygiene-reconciliation-queue-empty">
        <CardContent className="p-8 text-center text-slate-400">
          <p className="text-lg">No active Email Hygiene reconciliations</p>
          <p className="text-sm mt-2">Every recipient currently reflects an unambiguous provider outcome.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass border-white/10" data-testid="email-hygiene-reconciliation-queue-table">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="w-8 text-slate-400" />
                <TableHead className="text-slate-400">Type</TableHead>
                <TableHead className="text-slate-400">Recipient</TableHead>
                <TableHead className="text-slate-400">Environment</TableHead>
                <TableHead className="text-slate-400">Provider</TableHead>
                <TableHead className="text-slate-400">Candidate State</TableHead>
                <TableHead className="text-slate-400">Reason</TableHead>
                <TableHead className="text-slate-400">Recovery Status</TableHead>
                <TableHead className="text-right text-slate-400">Matched Contacts</TableHead>
                <TableHead className="text-right text-slate-400">Waiting</TableHead>
                <TableHead className="text-slate-400">Available Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const recordType = row.record_type || "native";
                const badge = RECORD_TYPE_BADGE[recordType] || RECORD_TYPE_BADGE.native;
                const candidateState = row.native?.candidate_hygiene_state || row.propagation?.canonical_hygiene_state;
                const classification = row.native?.classification;
                return (
                  <TableRow
                    key={row.reconciliation_id}
                    className="cursor-pointer border-white/10 hover:bg-white/5"
                    data-testid={`email-hygiene-reconciliation-row-${row.reconciliation_id}`}
                    onClick={() => onSelect?.(row.reconciliation_id)}
                  >
                    <TableCell>
                      <ChevronRight className="h-4 w-4 text-slate-400" data-testid={`email-hygiene-reconciliation-open-${row.reconciliation_id}`} />
                    </TableCell>
                    <TableCell data-testid={`email-hygiene-reconciliation-record-type-${row.reconciliation_id}`}>
                      <Badge className={`${badge.className} text-xs`}>{badge.label}</Badge>
                    </TableCell>
                    <TableCell className="max-w-[220px]">
                      <div className="truncate text-sm font-medium text-slate-100" title={row.recipient}>
                        {row.recipient}
                      </div>
                      {classification && (
                        <div className="text-xs text-slate-400">{classification}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm capitalize text-slate-300">{row.environment}</TableCell>
                    <TableCell className="text-sm capitalize text-slate-300">{row.provider}</TableCell>
                    <TableCell className="text-sm text-slate-300">{candidateState}</TableCell>
                    <TableCell className="text-sm text-slate-300">
                      {row.reconciliation_reason || <span className="text-slate-500">Unavailable</span>}
                    </TableCell>
                    <RecoveryStateCell
                      recoveryState={row.recovery_state}
                      testId={`email-hygiene-reconciliation-recovery-state-${row.reconciliation_id}`}
                    />
                    <TableCell className="text-right font-mono text-slate-100" data-testid={`email-hygiene-reconciliation-matched-${row.reconciliation_id}`}>
                      {row.matched_contact_count}
                    </TableCell>
                    <TableCell
                      className="text-right text-sm text-slate-300"
                      title={row.entered_reconciliation_at ? new Date(row.entered_reconciliation_at).toISOString() : undefined}
                    >
                      {formatRelativeAge(row.entered_reconciliation_at)}
                    </TableCell>
                    <AvailableActionCell
                      actions={row.available_actions}
                      testId={`email-hygiene-reconciliation-action-${row.reconciliation_id}`}
                    />
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
